'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { ArrowRight, CheckCircle2, Loader2, Zap } from 'lucide-react';
import { profileApi, type PlanDto } from '@/lib/api';
import { LANDING_PLANS_FALLBACK } from '@/lib/landing-plans-fallback';

const PLANS_FETCH_TIMEOUT_MS = 8_000;

function formatCurrency(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/** Trial é ativado no cadastro, não como card de checkout na LP. */
function plansForLanding(all: PlanDto[]): PlanDto[] {
  const visible = all.filter((p) => p.name !== 'Trial');
  return visible.length > 0 ? visible : LANDING_PLANS_FALLBACK;
}

export default function LandingPlansSection() {
  const [plans, setPlans] = useState<PlanDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [usingFallback, setUsingFallback] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const list = await profileApi.listPlansPublic({ timeoutMs: PLANS_FETCH_TIMEOUT_MS });
        if (cancelled) return;
        const visible = plansForLanding(list);
        if (visible.length > 0) {
          setPlans(visible);
          setUsingFallback(false);
        } else {
          setPlans(LANDING_PLANS_FALLBACK);
          setUsingFallback(true);
        }
      } catch {
        if (!cancelled) {
          setPlans(LANDING_PLANS_FALLBACK);
          setUsingFallback(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section id="planos" className="border-y border-border bg-muted/20 py-16 sm:py-20">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="mx-auto max-w-2xl text-center">
          <span className="badge border-primary/20 bg-primary/10 text-primary">Planos</span>
          <h2 className="mt-4 text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
            Escolha o plano ideal para você
          </h2>
          <p className="mt-3 text-sm text-muted-foreground sm:text-base">
            Comece com 14 dias grátis ao criar sua conta. Planos pagos com checkout seguro — cancele
            quando quiser.
          </p>
          {usingFallback && !loading && (
            <p className="mt-2 text-xs text-warning">
              Exibindo valores de referência — conecte o backend para preços atualizados em tempo
              real.
            </p>
          )}
        </div>

        {loading ? (
          <div className="card mx-auto mt-10 flex max-w-md items-center justify-center gap-2 px-5 py-14 text-sm text-muted-foreground">
            <Loader2 size={18} className="animate-spin text-primary" />
            Carregando planos…
          </div>
        ) : (
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:gap-5">
            {plans.map((plan) => (
              <article
                key={plan.id}
                className={`card flex flex-col p-6 transition-all hover:shadow-elevated ${
                  plan.popular ? 'border-2 border-primary ring-1 ring-primary/10' : ''
                }`}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-lg font-bold text-foreground">{plan.name}</h3>
                  {plan.popular && (
                    <span className="badge border-warning bg-warning-bg text-warning text-[10px] uppercase">
                      Popular
                    </span>
                  )}
                  {plan.popular && <Zap size={14} className="text-accent" aria-hidden />}
                </div>

                <div className="mt-3 flex items-baseline gap-1">
                  <span className="font-mono-nums text-3xl font-extrabold text-foreground">
                    {plan.value > 0 ? formatCurrency(plan.value) : 'Grátis'}
                  </span>
                  {plan.value > 0 && <span className="text-sm text-muted-foreground">/mês</span>}
                </div>

                {plan.freeTrialDays > 0 && (
                  <p className="mt-1 text-xs font-medium text-primary">
                    {plan.freeTrialDays === 1
                      ? '1 dia grátis para testar'
                      : `${plan.freeTrialDays} dias grátis para testar`}
                  </p>
                )}

                {plan.description && (
                  <p className="mt-2 text-sm text-muted-foreground">{plan.description}</p>
                )}

                <ul className="mt-5 flex-1 space-y-2">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm text-foreground">
                      <CheckCircle2
                        size={16}
                        className="mt-0.5 shrink-0 text-success"
                        aria-hidden
                      />
                      {feature}
                    </li>
                  ))}
                </ul>

                <Link
                  href="/login"
                  className={`mt-6 inline-flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-all active:scale-[0.98] ${
                    plan.popular
                      ? 'bg-primary text-primary-foreground hover:bg-blue-700'
                      : 'btn-secondary'
                  }`}
                >
                  {plan.value > 0 ? 'Começar com este plano' : 'Criar conta grátis'}
                  <ArrowRight size={16} aria-hidden />
                </Link>
              </article>
            ))}
          </div>
        )}

        <p className="mt-8 text-center text-xs text-muted-foreground">
          Pagamento seguro · Sem taxa de adesão · Trial de 14 dias no primeiro cadastro
        </p>
      </div>
    </section>
  );
}
